/**
 * In-browser OIDC authorization-code + PKCE flow for the public client
 * `twads-spa`. Mirrors g1-ads/apps/spa/src/lib/auth.ts: verifier and
 * state are stashed in sessionStorage; the token lives in localStorage so the
 * authed fetch wrapper can attach it as a Bearer header.
 */

import {
	AUTH_ISSUER,
	OIDC_AUTHORIZE_URL,
	OIDC_CLIENT_ID,
	OIDC_REDIRECT_PATH,
	OIDC_SCOPE,
	OIDC_TOKEN_URL,
} from "../config.ts";
import { challengeS256, newState, newVerifier } from "./pkce.ts";

const TOKEN_KEY = "twads_access_token";
const TOKEN_EXP_KEY = "twads_access_token_exp";
const VERIFIER_KEY = "twads_pkce_verifier";
const STATE_KEY = "twads_pkce_state";
const RETURN_KEY = "twads_return_to";
const ATTEMPT_KEY = "twads_silent_attempts";
const SIGNED_OUT_KEY = "twads_signed_out";

function redirectUri(): string {
	return `${window.location.origin}${OIDC_REDIRECT_PATH}`;
}

/**
 * Whether to send someone straight through the authorize round trip instead of
 * showing a sign-in button.
 *
 * The access token lives an hour and this client has no refresh grant, so
 * without this a signed-in operator is asked to "sign in" again every hour even
 * though their g1-auth session is perfectly valid. The round trip is invisible
 * when it is: first-party client, live session cookie, no consent screen.
 *
 * Two brakes: an explicit sign-out suppresses it until the next deliberate
 * click, and repeated failures fall back to the manual screen rather than
 * bouncing the browser between two hosts forever.
 */
export function shouldTrySilentLogin(): boolean {
	if (sessionStorage.getItem(SIGNED_OUT_KEY)) return false;
	return Number(sessionStorage.getItem(ATTEMPT_KEY) ?? "0") < 2;
}

export function noteSilentAttempt(): void {
	const n = Number(sessionStorage.getItem(ATTEMPT_KEY) ?? "0");
	sessionStorage.setItem(ATTEMPT_KEY, String(n + 1));
}

/** A deliberate click outranks both brakes — that is what makes it deliberate. */
export async function signInClicked(): Promise<void> {
	clearSilentState();
	await beginLogin("/");
}

function clearSilentState(): void {
	sessionStorage.removeItem(ATTEMPT_KEY);
	sessionStorage.removeItem(SIGNED_OUT_KEY);
}

export function getToken(): string | null {
	const token = localStorage.getItem(TOKEN_KEY);
	if (!token) return null;
	const exp = Number(localStorage.getItem(TOKEN_EXP_KEY) ?? "0");
	if (exp && Date.now() >= exp) {
		clearToken();
		return null;
	}
	return token;
}

export function clearToken(): void {
	localStorage.removeItem(TOKEN_KEY);
	localStorage.removeItem(TOKEN_EXP_KEY);
}

function sanitizeReturn(input: string | null | undefined): string {
	if (!input) return "/";
	if (!input.startsWith("/") || input.startsWith("//")) return "/";
	if (input.startsWith(OIDC_REDIRECT_PATH)) return "/";
	return input;
}

/** Kick off the authorize redirect. `returnTo` is restored after callback. */
export async function beginLogin(returnTo?: string): Promise<void> {
	const verifier = newVerifier();
	const challenge = await challengeS256(verifier);
	const state = newState();

	sessionStorage.setItem(VERIFIER_KEY, verifier);
	sessionStorage.setItem(STATE_KEY, state);
	sessionStorage.setItem(
		RETURN_KEY,
		sanitizeReturn(returnTo ?? window.location.pathname + window.location.search),
	);

	const authorize = new URL(OIDC_AUTHORIZE_URL);
	authorize.searchParams.set("response_type", "code");
	authorize.searchParams.set("client_id", OIDC_CLIENT_ID);
	authorize.searchParams.set("redirect_uri", redirectUri());
	authorize.searchParams.set("scope", OIDC_SCOPE);
	authorize.searchParams.set("state", state);
	authorize.searchParams.set("code_challenge", challenge);
	authorize.searchParams.set("code_challenge_method", "S256");

	window.location.assign(authorize.toString());
}

export interface CallbackResult {
	returnTo: string;
}

/** Handle the /auth/callback redirect: validate state, exchange code→token. */
export async function completeCallback(): Promise<CallbackResult> {
	const url = new URL(window.location.href);
	const code = url.searchParams.get("code");
	const stateParam = url.searchParams.get("state");
	const err = url.searchParams.get("error");

	if (err) throw new Error(`oauth_error: ${err}`);
	if (!code || !stateParam) throw new Error("missing_code_or_state");

	const verifier = sessionStorage.getItem(VERIFIER_KEY);
	const expectedState = sessionStorage.getItem(STATE_KEY);
	const returnTo = sanitizeReturn(sessionStorage.getItem(RETURN_KEY));
	if (!verifier || !expectedState) throw new Error("missing_pkce_state");
	if (stateParam !== expectedState) throw new Error("state_mismatch");

	const tokenRes = await fetch(OIDC_TOKEN_URL, {
		method: "POST",
		headers: { "content-type": "application/x-www-form-urlencoded" },
		body: new URLSearchParams({
			grant_type: "authorization_code",
			code,
			client_id: OIDC_CLIENT_ID,
			redirect_uri: redirectUri(),
			code_verifier: verifier,
		}),
	});

	if (!tokenRes.ok) {
		const body = await tokenRes.text();
		throw new Error(`token_exchange_failed: ${tokenRes.status} ${body.slice(0, 200)}`);
	}
	const tokens = (await tokenRes.json()) as {
		access_token?: string;
		expires_in?: number;
	};
	if (!tokens.access_token) throw new Error("no_access_token_in_response");

	// Expire a minute early so a request is never sent with a token that dies
	// in flight, which would surface as a spurious 401.
	const ttl = Math.max(60, Math.min(tokens.expires_in ?? 3600, 86400));
	localStorage.setItem(TOKEN_KEY, tokens.access_token);
	localStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + (ttl - 60) * 1000));

	sessionStorage.removeItem(VERIFIER_KEY);
	sessionStorage.removeItem(STATE_KEY);
	sessionStorage.removeItem(RETURN_KEY);
	clearSilentState();

	return { returnTo };
}

/**
 * End the session here and across g1-auth.
 *
 * Signing out must stick: without dropping the shared session cookie the silent
 * login would walk the operator straight back in, which is the opposite of what
 * the button says. The suppression flag covers the case where the cookie
 * outlives the request.
 */
export async function logout(): Promise<void> {
	clearToken();
	sessionStorage.setItem(SIGNED_OUT_KEY, "1");
	try {
		await fetch(`${AUTH_ISSUER}/auth/logout`, { method: "POST", credentials: "include" });
	} catch {
		// Best effort — the local token is already gone either way.
	}
	window.location.replace("/");
}
